<template>
  <v-container fluid tag="section">
    <base-material-card
      color="indigo"
      icon="mdi-account-plus"
      inline
      :class="['elevation-4', 'user-form-card', $vuetify.breakpoint.xs ? 'px-3 py-3' : 'px-5 py-4']"
    >
      <template v-slot:after-heading>
        <div :class="$vuetify.breakpoint.xs ? 'text-h4' : 'display-2'" class="font-weight-light">
          New User
        </div>
      </template>


      <formulate-form ref="userForm" @submit="save" :errors="inputErrors" :form-errors="formErrors">
        <div class="top-right-save">
          <v-btn
            type="submit"
            color="indigo"
            dark
            class="save-user-btn"
            :disabled="!isSaveReady"
          >
            <v-icon left small>mdi-content-save</v-icon>
            Save User
          </v-btn>
        </div>
        <v-row class="form-row">
          <!-- User Information Card -->
          <v-col cols="12" md="6" lg="4">
            <v-card class="form-section-card" elevation="2">
              <v-card-title class="section-title">
                <v-icon left color="indigo">mdi-account</v-icon>
                User Information
              </v-card-title>
              <v-card-text class="pt-3 user-info-fields">
                <formulate-input
                  v-model="userData.email"
                  type="email"
                  name="email"
                  label="Email"
                  placeholder="Enter email address"
                  validation="required|email"
                  error-behavior="blur"
                />

                <formulate-input
                  v-model="userData.password"
                  label="Password"
                  type="password"
                  name="password"
                  placeholder="Enter password"
                  error-behavior="blur"
                  validation="required"
                />

                <formulate-input
                  v-model="userData.system_domain"
                  type="text"
                  name="system_domain"
                  label="System Domain"
                  placeholder="testdomain"
                  validation="required"
                  error-behavior="blur"
                />

                <div class="checkbox-field">
                  <div class="formulate-input-label">Select Services</div>
                  <div class="custom-checkbox-list">
                    <label
                      v-for="service in services"
                      :key="service.value"
                      class="custom-checkbox"
                    >
                      <input
                        v-model="selectedServices"
                        type="checkbox"
                        :value="service.value"
                        class="custom-checkbox__input"
                      />
                      <span class="custom-checkbox__box">
                        <v-icon size="14" class="custom-checkbox__check">mdi-check</v-icon>
                      </span>
                      <span class="custom-checkbox__label">{{ service.label }}</span>
                    </label>
                  </div>
                </div>

                <formulate-input
                  v-model="userData.allowManual"
                  :options="{ true: 'Yes', false: 'No' }"
                  type="radio"
                  for="allowManual"
                  label="Allow Manual Booking?"
                  :outer-class="(context, classes) => ['allow-manual-field'].concat(classes)"
                />

                <div class="checkbox-field feature-access-field">
                  <div class="formulate-input-label">Feature Access</div>
                  <div class="custom-checkbox-list">
                    <label
                      v-for="feature in features"
                      :key="feature.value"
                      class="custom-checkbox"
                    >
                      <input
                        v-model="selectedFeatures"
                        type="checkbox"
                        :value="feature.value"
                        class="custom-checkbox__input"
                      />
                      <span class="custom-checkbox__box">
                        <v-icon size="14" class="custom-checkbox__check">mdi-check</v-icon>
                      </span>
                      <span class="custom-checkbox__label">{{ feature.label }}</span>
                    </label>
                  </div>
                </div>

              </v-card-text>
            </v-card>
          </v-col>

          <!-- Provider & API Configuration Card -->
          <v-col cols="12" md="6" lg="4">
            <v-card class="form-section-card" elevation="2">
              <v-card-title class="section-title">
                <v-icon left color="indigo">mdi-server-network</v-icon>
                Provider & API
              </v-card-title>
              <v-card-text class="pt-3">
                <formulate-input
                  v-model="selectedProvider"
                  type="radio"
                  label="Select Provider"
                  name="selectedProvider"
                  :options="{ autocab: 'Autocab', infocab: 'Infocab', icabbi: 'iCabbi' }"
                  for="selectedProvider"
                  :outer-class="(context, classes) => ['mb-0'].concat(classes)"
                  :element-class="(context, classes) => ['sms-gateway-element-class', 'provider-select-input'].concat(classes)"
                />


                <!-- Autocab Form -->
                <formulate-form v-if="selectedProvider === 'autocab'" @submit="fetchCompanies">
                  <div class="credentials-alert credentials-alert-green mb-4">
                    <v-icon color="indigo" class="mr-2">mdi-information-outline</v-icon>
                    <span class="credentials-alert-text ">Enter your Autocab API credentials</span>
                  </div>
                  <formulate-input
                    v-model="userData.api_username"
                    type="text"
                    name="api_username"
                    label="API Username"
                    placeholder="Enter API username"
                    validation="required"
                    error-behavior="blur"
                  />
                  <formulate-input
                    v-model="userData.api_password"
                    type="password"
                    name="api_password"
                    label="API Password"
                    placeholder="Enter API password"
                    validation="required"
                    error-behavior="blur"
                  />
                  <formulate-input
                    v-model="userData.api_host"
                    type="url"
                    name="api_host"
                    label="API Host"
                    placeholder="Enter API Host URL"
                    validation="required"
                    error-behavior="blur"
                  />
                  <v-btn
                    type="submit"
                    color="success"
                    block
                    :disabled="disableFetchButton"
                    :loading="disableFetchButton"
                    class="fetch-btn"
                  >
                    <v-icon left>mdi-download</v-icon>
                    Fetch Companies
                  </v-btn>
                </formulate-form>

                <!-- Infocab Form -->
                <formulate-form v-else-if="selectedProvider === 'infocab'" @submit="fetchInfoCompanies">
                  <div class="credentials-alert credentials-alert-green mb-4">
                    <v-icon color="indigo" class="mr-2">mdi-information-outline</v-icon>
                    <span class="credentials-alert-text">Enter your Infocab API credentials</span>
                  </div>
                  <formulate-input
                    v-model="userData.infocab_host"
                    type="url"
                    name="infocab_host"
                    label="Infocab Host"
                    placeholder="Enter Infocab host URL"
                    validation="required"
                    error-behavior="blur"
                  />
                  <formulate-input
                    v-model="userData.infocab_token"
                    type="text"
                    name="infocab_token"
                    label="Infocab Token"
                    placeholder="Enter Infocab Token"
                    validation="required"
                    error-behavior="blur"
                  />
                  <v-btn
                    type="submit"
                    color="success"
                    block
                    :disabled="disableInfoFetchButton"
                    :loading="disableInfoFetchButton"
                    class="fetch-btn"
                  >
                    <v-icon left>mdi-download</v-icon>
                    Fetch Companies
                  </v-btn>
                </formulate-form>

                <!-- iCabbi Form -->
                <formulate-form v-else-if="selectedProvider === 'icabbi'" @submit="fetchICabbiCompanies">
                  <div class="credentials-alert credentials-alert-green mb-4">
                    <v-icon color="indigo" class="mr-2">mdi-information-outline</v-icon>
                    <span class="credentials-alert-text">Enter your iCabbi API credentials</span>
                  </div>
                  <formulate-input
                    v-model="userData.icabbi_host"
                    type="url"
                    name="icabbi_host"
                    label="iCabbi URL"
                    placeholder="Enter iCabbi URL"
                    validation="required"
                    error-behavior="blur"
                  />
                  <formulate-input
                    v-model="userData.icabbi_token"
                    type="text"
                    name="icabbi_token"
                    label="iCabbi Token"
                    placeholder="Enter iCabbi Token"
                    validation="required"
                    error-behavior="blur"
                  />
                  <v-btn
                    type="submit"
                    color="success"
                    block
                    :disabled="disableICabbiFetchButton"
                    :loading="disableICabbiFetchButton"
                    class="fetch-btn"
                  >
                    <v-icon left>mdi-download</v-icon>
                    Fetch Companies
                  </v-btn>
                </formulate-form>

                <v-divider class="my-4" v-if="companies.length > 0" />

                <!-- Companies Selection -->
                <div v-if="companies.length > 0">
                  <div class="formulate-input-label companies-field-label">
                    Companies
                  </div>
                  <v-autocomplete
                    v-model="selectedCompanies"
                    :items="companies"
                    item-text="label"
                    item-value="value"
                    color="success"
                    item-color="success"
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    dense
                    outlined
                    hide-details
                    class="companies-picker"
                    placeholder="Search and select companies"
                    :menu-props="{ maxHeight: 320 }"
                  />

                  <div v-if="selectedCompanies && selectedCompanies.length" class="judo-compact mt-3">
                    <div class="d-flex align-center justify-space-between mb-2">
                      <div class="text-caption font-weight-medium grey--text text--darken-2">
                        Payment credentials
                      </div>
                      <div class="text-caption grey--text">
                        Click to add/edit
                      </div>
                    </div>

                    <div class="judo-list">
                      <div v-for="sc in selectedCompanies" :key="sc" class="judo-row">
                        <div class="judo-company-name" :title="sc">{{ sc }}</div>
                        <v-btn
                          x-small
                          outlined
                          :color="hasPaymentAccount(sc) ? 'success' : 'indigo'"
                          class="judo-action-btn"
                          @click="openPaymentAccountDialog(sc)"
                        >
                          <v-icon x-small left>
                            {{ hasPaymentAccount(sc) ? 'mdi-check-circle' : 'mdi-credit-card-outline' }}
                          </v-icon>
                          {{ getPaymentButtonLabel(sc) }}
                        </v-btn>
                      </div>
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- SMS Gateway Configuration Card -->
          <v-col cols="12" md="6" lg="4">
            <v-card class="form-section-card" elevation="2">
              <v-card-title class="section-title">
                <v-icon left color="indigo">mdi-message-text</v-icon>
                SMS Gateway
              </v-card-title>
              <v-card-text class="pt-3">
                <formulate-input
                  v-model="userData.sms_service"
                  type="radio"
                  label="SMS Method"
                  name="payment"
                  :options="{ twillio: 'Twilio', whatsApp: 'WhatsApp/URL' }"
                  for="sms"
                  :outer-class="(context, classes) => ['mb-0'].concat(classes)"
                  :element-class="(context, classes) => ['sms-gateway-element-class', 'provider-select-input'].concat(classes)"
               />

                <!-- Twilio Configuration -->
                <formulate-form v-show="userData.sms_service == 'twillio'" type="group">

                  <div class="credentials-alert credentials-alert-green mb-4">
                    <v-icon color="indigo" class="mr-2">mdi-information-outline</v-icon>
                    <span class="credentials-alert-text">Enter your Twilio credentials</span>
                  </div>
                  <formulate-input
                    v-model="userData.twillio.sid"
                    type="text"
                    name="twillio_sid"
                    label="Twilio SID"
                    placeholder="Enter Twilio SID"
                    validation="twillio_validation"
                    :validation-rules="{
                      twillio_validation: ({ value }) =>
                        !(userData.twillio.sid == '' && userData.sms_service == 'twillio'),
                    }"
                    :validation-messages="{
                      twillio_validation: 'SID is required.',
                    }"
                    error-behavior="blur"
                  />
                  <formulate-input
                    v-model="userData.twillio.auth"
                    type="text"
                    name="twillio_auth"
                    label="Twilio Auth Token"
                    placeholder="Enter Auth Token"
                    validation="twillio_validation"
                    :validation-rules="{
                      twillio_validation: ({ value }) =>
                        !(userData.twillio.auth == '' && userData.sms_service == 'twillio'),
                    }"
                    :validation-messages="{
                      twillio_validation: 'Auth token is required.',
                    }"
                    error-behavior="blur"
                  />
                  <formulate-input
                    v-model="userData.twillio.number"
                    type="text"
                    name="twillio_number"
                    label="Twilio Number"
                    placeholder="Enter Twilio Number"
                    validation="twillio_validation"
                    :validation-rules="{
                      twillio_validation: ({ value }) =>
                        !(userData.twillio.number == '' && userData.sms_service == 'twillio'),
                    }"
                    :validation-messages="{
                      twillio_validation: 'Number is required.',
                    }"
                    error-behavior="blur"
                  />
                </formulate-form>

                <!-- WhatsApp Configuration -->
                <formulate-form v-show="userData.sms_service == 'whatsApp'" type="group">
                  <div class="credentials-alert credentials-alert-green mb-3">
                    <v-icon color="indigo" class="mr-2">mdi-information-outline</v-icon>
                    <span class="credentials-alert-text">Enter your WhatsApp/URL credentials</span>
                  </div>
                  <formulate-input
                    v-model="userData.whatsApp.link"
                    name="link"
                    label="URL Link"
                    placeholder="Enter URL link"
                    validation="twillio_validation"
                    :validation-rules="{
                      twillio_validation: ({ value }) =>
                        !(userData.whatsApp.link == '' && userData.sms_service == 'whatsApp'),
                    }"
                    :validation-messages="{
                      twillio_validation: 'URL link is required.',
                    }"
                    error-behavior="blur"
                  />
                  <formulate-input
                    v-model="userData.whatsApp.password"
                    type="text"
                    name="password"
                    label="URL Password"
                    placeholder="Enter URL password"
                    validation="twillio_validation"
                    :validation-rules="{
                      twillio_validation: ({ value }) =>
                        !(userData.whatsApp.password == '' && userData.sms_service == 'whatsApp'),
                    }"
                    :validation-messages="{
                      twillio_validation: 'URL link password is required.',
                    }"
                    error-behavior="blur"
                  />
                </formulate-form>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Capabilities Section (Autocab + Infocab) — full-width -->
          <v-col v-if="supportsCapabilities" cols="12">
            <v-card class="form-section-card capabilities-card" elevation="2">
              <v-card-title class="section-title capabilities-header">
                <div class="capabilities-header__left">
                  <v-icon left color="indigo">mdi-puzzle-outline</v-icon>
                  Capabilities
                  <v-chip
                    v-if="selectedCapabilities && selectedCapabilities.length"
                    x-small
                    color="indigo"
                    text-color="white"
                    class="ml-2 cap-count-chip"
                  >
                    {{ selectedCapabilities.length }} selected
                  </v-chip>
                </div>
                <v-btn
                  v-if="capabilities.length > 0"
                  small
                  outlined
                  color="indigo"
                  class="cap-refresh-btn"
                  :disabled="disableCapabilitiesFetchButton"
                  :loading="disableCapabilitiesFetchButton"
                  @click="fetchCapabilities"
                >
                  <v-icon left small>mdi-refresh</v-icon>
                  Refresh
                </v-btn>
              </v-card-title>
              <v-card-text class="pt-4 capabilities-card-body">
                <!-- Empty state: nothing fetched yet -->
                <div v-if="capabilities.length === 0" class="cap-empty">
                  <div class="cap-empty__icon">
                    <v-icon size="36" color="indigo lighten-2">mdi-puzzle-outline</v-icon>
                  </div>
                  <div class="cap-empty__title">No capabilities loaded yet</div>
                  <div class="cap-empty__subtitle">
                    {{ capabilitiesEmptySubtitle }}
                  </div>
                  <v-btn
                    color="success"
                    class="fetch-btn mt-4"
                    :disabled="disableCapabilitiesFetchButton"
                    :loading="disableCapabilitiesFetchButton"
                    @click="fetchCapabilities"
                  >
                    <v-icon left small>mdi-download</v-icon>
                    Fetch Capabilities
                  </v-btn>
                </div>

                <template v-else>
                  <div class="cap-picker">
                    <div class="formulate-input-label companies-field-label">
                      Select Capabilities
                    </div>
                    <v-autocomplete
                      v-model="selectedCapabilities"
                      :items="capabilities"
                      item-text="label"
                      item-value="id"
                      color="success"
                      item-color="success"
                      multiple
                      chips
                      small-chips
                      deletable-chips
                      dense
                      outlined
                      hide-details
                      class="companies-picker"
                      placeholder="Search and select capabilities"
                      :menu-props="{ maxHeight: 320 }"
                    >
                      <template v-slot:item="{ item }">
                        <div class="capability-option">
                          <span
                            v-if="item.colour"
                            class="capability-swatch"
                            :style="capabilityColourStyle(item.colour)"
                          />
                          <span>{{ item.label }}</span>
                        </div>
                      </template>
                    </v-autocomplete>
                  </div>

                  <div
                    v-if="selectedCapabilities && selectedCapabilities.length"
                    class="cap-grid-wrap mt-4"
                  >
                    <div class="d-flex align-center justify-space-between flex-wrap mb-2">
                      <div class="text-caption font-weight-medium grey--text text--darken-2">
                        Capability logos
                      </div>
                      <div class="text-caption grey--text">
                        Click a tile to upload · PNG, JPG/JPEG, SVG · max 2MB
                      </div>
                    </div>

                    <div class="cap-grid">
                      <div
                        v-for="capId in selectedCapabilities"
                        :key="capId"
                        class="cap-tile"
                        :class="{ 'cap-tile--filled': !!capabilityImages[capId] }"
                      >
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <span class="cap-tile__deselect-wrap" v-bind="attrs" v-on="on">
                              <v-btn
                                icon
                                x-small
                                class="cap-tile__deselect"
                                aria-label="Remove vehicle"
                                @click.stop="removeCapability(capId)"
                              >
                                <v-icon size="14">mdi-close</v-icon>
                              </v-btn>
                            </span>
                          </template>
                          <span>Remove vehicle</span>
                        </v-tooltip>

                        <div
                          class="cap-tile__logo"
                          :class="{ 'cap-tile__logo--uploading': capabilityImageUploading[capId] }"
                          role="button"
                          tabindex="0"
                          @click="triggerCapabilityImagePick(capId)"
                          @keydown.enter="triggerCapabilityImagePick(capId)"
                        >
                          <template v-if="capabilityImageUploading[capId]">
                            <v-progress-circular indeterminate size="22" width="2" color="indigo" />
                            <span class="cap-tile__hint">Uploading…</span>
                          </template>
                          <template v-else-if="capabilityImages[capId]">
                            <img :src="capabilityImages[capId]" alt="Capability logo" />
                            <div class="cap-tile__overlay">
                              <v-icon small color="white">mdi-upload</v-icon>
                              <span>Change logo</span>
                            </div>
                          </template>
                          <template v-else>
                            <v-icon size="24" color="grey lighten-1">mdi-image-plus</v-icon>
                            <span class="cap-tile__hint">Add logo</span>
                          </template>
                        </div>

                        <div class="cap-tile__body">
                          <span
                            v-if="capabilityColour(capId)"
                            class="capability-swatch cap-tile__swatch"
                            :style="capabilityColourStyle(capabilityColour(capId))"
                          />
                          <div class="cap-tile__name" :title="capabilityLabel(capId)">
                            {{ capabilityLabel(capId) }}
                          </div>
                          <v-tooltip v-if="capabilityImages[capId]" bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <span v-bind="attrs" v-on="on">
                                <v-btn
                                  icon
                                  x-small
                                  color="error"
                                  class="cap-tile__clear"
                                  aria-label="Remove image"
                                  @click.stop="clearCapabilityImage(capId)"
                                >
                                  <v-icon size="16">mdi-delete</v-icon>
                                </v-btn>
                              </span>
                            </template>
                            <span>Remove image</span>
                          </v-tooltip>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>

                <input
                  ref="capabilityImageInput"
                  type="file"
                  accept="image/png,image/jpeg,.jpg,.jpeg,.svg,image/svg+xml"
                  class="d-none"
                  @change="onCapabilityImageSelected"
                />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

      </formulate-form>
    </base-material-card>
    <v-dialog
      v-model="dialog"
      :max-width="$vuetify.breakpoint.xs ? '96%' : '700'"
      persistent
    >
      <v-card class="elevation-12 judo-dialog payment-dialog">
        <v-card-title class="payment-dialog__header">
          <div class="payment-dialog__heading-wrap">
            <div class="payment-dialog__title">Payment Credentials</div>
            <div class="payment-dialog__subtitle">
              {{ currentPaymentAccount.company }}
            </div>
          </div>
          <v-btn icon class="judo-close-btn" @click="dialog = false">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="payment-dialog__content">
          <formulate-form @submit="savePaymentCredentials">
            <div class="payment-section">
              <formulate-input
                v-model="currentPaymentAccount.provider"
                type="radio"
                label="Payment Provider *"
                name="payment_provider"
                :options="{ judo: 'Judo', revolut: 'Revolut' }"
                :help="'Choose provider to show relevant credential fields.'"
                :outer-class="(context, classes) => ['mb-2'].concat(classes)"
                :element-class="(context, classes) => ['payment-provider-radio'].concat(classes)"
              />
            </div>

            <v-fade-transition mode="out-in">
              <div :key="currentPaymentAccount.provider" class="payment-section payment-section--credentials">
                <div class="payment-section__title">
                  {{ currentPaymentAccount.provider === 'revolut' ? 'Revolut Credentials' : 'Judo Credentials' }}
                </div>
                <div class="payment-section__subtitle">
                  All fields below are required before saving.
                </div>

                <v-row dense class="mt-1">
                  <template v-if="currentPaymentAccount.provider === 'judo'">
                    <v-col cols="12" sm="6">
                      <formulate-input
                        v-model="currentPaymentAccount.judo_id"
                        type="text"
                        name="judo_id"
                        label="Judo ID *"
                        placeholder="e.g. 100123"
                        :help="'Your Judo account identifier.'"
                      />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <formulate-input
                        v-model="currentPaymentAccount.judo_api_version"
                        type="text"
                        name="judo_api_version"
                        label="Judo API Version *"
                        placeholder="e.g. v1"
                        :help="'Use the API version provided by Judo.'"
                      />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <formulate-input
                        v-model="currentPaymentAccount.judo_sandbox_url"
                        type="url"
                        name="judo_sandbox_url"
                        label="Judo URL *"
                        placeholder="https://..."
                        :help="'URL for Judo API requests.'"
                      />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <formulate-input
                        v-model="currentPaymentAccount.judo_auth_sandbox"
                        type="text"
                        name="judo_auth_sandbox"
                        label="Judo Auth *"
                        placeholder="Enter auth key"
                        :help="'Authorization credential from Judo.'"
                      />
                    </v-col>
                  </template>

                  <template v-else>
                    <v-col cols="12" sm="6" md="6">
                      <formulate-input
                        v-model="currentPaymentAccount.api_version"
                        type="text"
                        name="revolut_api_version"
                        label="API Version *"
                        placeholder="e.g. 2024-01-01"
                        :help="'Revolut API version for this account.'"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="6">
                      <formulate-input
                        v-model="currentPaymentAccount.base_url"
                        type="url"
                        name="revolut_base_url"
                        label="Base URL *"
                        placeholder="https://..."
                        :help="'Base endpoint for Revolut API requests.'"
                      />
                    </v-col>
                    <v-col cols="12" sm="12" md="12">
                      <formulate-input
                        v-model="currentPaymentAccount.api_secret"
                        type="text"
                        name="revolut_api_secret"
                        label="API Secret *"
                        placeholder="Enter API secret"
                        :help="'Keep this secret secure and up to date.'"
                        :outer-class="(context, classes) => ['payment-full-width-input'].concat(classes)"
                      />
                    </v-col>
                  </template>
                </v-row>
              </div>
            </v-fade-transition>

            <v-divider class="my-4" />

            <div class="payment-dialog__actions">
              <v-btn text class="payment-action-btn" @click="dialog = false">
                Cancel
              </v-btn>
              <v-btn
                v-if="editingPaymentAccount"
                color="error"
                outlined
                class="payment-action-btn payment-remove-btn"
                @click="removePaymentCredentials(currentPaymentAccount.company)"
              >
                <v-icon left small>mdi-delete</v-icon>
                Remove
              </v-btn>
              <v-btn
                type="submit"
                color="success"
                class="payment-action-btn"
                :disabled="!isCurrentPaymentAccountValid()"
              >
                <v-icon left small>mdi-content-save</v-icon>
                Save Credentials
              </v-btn>
            </div>
          </formulate-form>
        </v-card-text>
      </v-card>
    </v-dialog>
    <base-material-snackbar
      v-model="snackbar"
      type="error"
      class=""
      v-bind="{
        bottom: true,
        right: true,
      }"
      >{{ snackbarText }}</base-material-snackbar
    >
  </v-container>
</template>

<script>
import { _getCompanies, _getInfoCompanies, _saveAdmin, _getServices, _getICabbiCompanies, _getCapabilities, _getInfoFeatures, _uploadCapabilityImage } from '@/services/User'
import { setToken } from '@/services/Http'
import { validateLogoFile } from '@/utils/logo'
export default {
  name: 'AddUser',
  data: () => ({
    selectedProvider: 'autocab',
    formErrors: [],
    inputErrors: {},
    userData: {
      email: null,
      system_domain: '',
      password: null,
      api_username: 'journeypoint',
      api_password: 'Tax1976',
      api_host: 'https://ghost-main-static-a9f69c6af8414d208d369945002007a4.ghostapi.app:29003',
      sms_service: 'twillio',
      twillio: {
        sid: '',
        auth: '',
        number: '',
      },
      infocab_host: 'https://stage-webbookerapi.infocabs.com',
      infocab_token: 'n0XynNayLlVW8vDsoG59MydnLUj5iVrkjrLgbZqU7HA=',
      icabbi_host: 'https://api.coolnagour.com/v2/',
      icabbi_token: 'NTJlMWExYzE2MjczYTc1YzdiNWU1MWVhYzY5YjdiNGIyMDg5ZmFkYzo4ZjYzYWY4NDU3ZmRlMjU1YWVjMTlmZjQ3NjYwMmU5MmQ1NmY0YTcw',
      whatsApp: {
        link: '',
        password: '',
      },
      allowManual: true,
      capabilities: [],
    },
    companies: [],
    selectedCompanies: [],
    capabilities: [],
    selectedCapabilities: [],
    capabilityImages: {},
    capabilityIcons: {},
    capabilityImageUploading: {},
    capabilityImageTargetId: null,
    services: [],
    selectedServices: [],
    features: [
      { value: 'venue_qr_booker', label: 'Venue QR Booker' },
      { value: 'nudgpay_tracking', label: 'Nudgpay Tracking' },
      { value: 'nudgpay_editing', label: 'Nudgpay Editing' },
      { value: 'nudgpay_vehicle_type', label: 'Nudgpay Vehicle Type' },
    ],
    selectedFeatures: [],
    disableFetchButton: false,
    disableInfoFetchButton: false,
    disableICabbiFetchButton: false,
    disableCapabilitiesFetchButton: false,
    apiError: '',
    snackbar: false,
    snackbarText: ' Email already exists',
    show: false,
    dialog: false,

    currentPaymentAccount: {
      company: '',
      provider: 'judo',
      judo_id: '',
      judo_sandbox_url: '',
      judo_api_version: '',
      judo_auth_sandbox: '',
      api_version: '',
      base_url: '',
      api_secret: '',
    },
    selectedCompanyPaymentAccounts: [],
    editingPaymentAccount: false,
  }),
  computed: {
    supportsCapabilities () {
      return this.selectedProvider === 'autocab' || this.selectedProvider === 'infocab'
    },
    capabilitiesEmptySubtitle () {
      if (this.selectedProvider === 'infocab') {
        return 'Capabilities are Infocab features, fetched using the Infocab host and token entered above.'
      }
      return 'Capabilities are fetched from Autocab using the API credentials entered above.'
    },
    isSaveReady () {
      const has = (v) => v !== null && v !== undefined && String(v).trim() !== ''

      const emailOk = has(this.userData.email)
      const systemDomainOk = has(this.userData.system_domain)
      const passwordOk = has(this.userData.password)
      const servicesOk = Array.isArray(this.selectedServices) && this.selectedServices.length > 0
      const companiesOk = Array.isArray(this.selectedCompanies) && this.selectedCompanies.length > 0

      const provider = this.selectedProvider
      const providerOk = has(provider)
      let providerCredsOk = false
      if (provider === 'autocab') {
        providerCredsOk = has(this.userData.api_username) && has(this.userData.api_password) && has(this.userData.api_host)
      } else if (provider === 'infocab') {
        providerCredsOk = has(this.userData.infocab_host) && has(this.userData.infocab_token)
      } else if (provider === 'icabbi') {
        providerCredsOk = has(this.userData.icabbi_host) && has(this.userData.icabbi_token)
      }

      const sms = this.userData.sms_service
      let smsCredsOk = false
      if (sms === 'twillio') {
        smsCredsOk =
          has(this.userData.twillio && this.userData.twillio.sid) &&
          has(this.userData.twillio && this.userData.twillio.auth) &&
          has(this.userData.twillio && this.userData.twillio.number)
      } else if (sms === 'whatsApp') {
        smsCredsOk =
          has(this.userData.whatsApp && this.userData.whatsApp.link) &&
          has(this.userData.whatsApp && this.userData.whatsApp.password)
      }

      return emailOk && systemDomainOk && passwordOk && servicesOk && providerOk && providerCredsOk && companiesOk && smsCredsOk
    },
  },
  mounted() {
    setToken()
    this.getServices()
  },
  watch: {
    selectedProvider(newProvider) {
      this.companies = [];
      this.selectedCompanies = [];
      this.capabilities = [];
      this.selectedCapabilities = [];
      this.capabilityImages = {};
      this.capabilityIcons = {};
      // Emit event or perform logic based on selection
      console.log('Provider switched to:', newProvider)
      this.$emit('provider-changed', newProvider)
    },
    selectedCompanies(newval) {
      this.selectedCompanyPaymentAccounts = this.selectedCompanyPaymentAccounts.filter((f) =>
        newval.includes(f.company)
      )
    },
    selectedCapabilities(newval) {
      const ids = Array.isArray(newval) ? newval : []
      const nextImages = {}
      const nextIcons = {}
      ids.forEach((id) => {
        const imageKey = this.capabilityMediaKey(id, this.capabilityImages)
        const iconKey = this.capabilityMediaKey(id, this.capabilityIcons)
        if (imageKey != null) nextImages[id] = this.capabilityImages[imageKey]
        if (iconKey != null) nextIcons[id] = this.capabilityIcons[iconKey]
      })
      this.capabilityImages = nextImages
      this.capabilityIcons = nextIcons
    },
  },

  methods: {
    capabilityMediaKey (capId, map) {
      if (!map || typeof map !== 'object') return null
      if (Object.prototype.hasOwnProperty.call(map, capId)) return capId
      if (Object.prototype.hasOwnProperty.call(map, String(capId))) return String(capId)
      const asNum = Number(capId)
      if (!Number.isNaN(asNum) && Object.prototype.hasOwnProperty.call(map, asNum)) return asNum
      return null
    },
    getCapabilityIconUrl (capId) {
      const key = this.capabilityMediaKey(capId, this.capabilityImages)
      if (key == null) return null
      return this.capabilityImages[key] || null
    },
    getCapabilityIcon (capId) {
      const key = this.capabilityMediaKey(capId, this.capabilityIcons)
      if (key == null) return null
      return this.capabilityIcons[key] || null
    },
    isCapabilitySelected (capId) {
      return (this.selectedCapabilities || []).some((id) => String(id) === String(capId))
    },
    mapCapability (d) {
      const shortCode = d.shortCode == null ? '' : String(d.shortCode)
      const name = d.name || ''
      const label = shortCode && shortCode !== '-' ? `${shortCode} — ${name}` : name
      return {
        id: d.id,
        name,
        shortCode,
        label,
        requirement: d.requirement,
        enabled: d.enabled,
        priority: d.priority,
        colour: d.colour,
        excludeFromBroadcast: d.excludeFromBroadcast,
        exclusiveCapability: d.exclusiveCapability,
        operatorOverride: d.operatorOverride,
        rowVersion: d.rowVersion,
      }
    },
    mapInfoFeature (d) {
      const id = d.Featureid != null ? d.Featureid : (d.featureid != null ? d.featureid : d.id)
      const name = d.name || ''
      return {
        id,
        Featureid: id,
        featureTypeId: d.featureTypeId,
        bitMask: d.bitMask,
        name,
        label: name,
      }
    },
    extractInfoFeatures (response) {
      const body = response && response.data ? response.data : {}
      const obj = body.obj
      if (Array.isArray(obj)) return obj
      if (obj && Array.isArray(obj.features)) return obj.features
      if (obj && Array.isArray(obj.capabilities)) return obj.capabilities
      if (Array.isArray(body)) return body
      return []
    },
    capabilityColourStyle (colour) {
      if (!colour) return {}
      return {
        backgroundColor: `rgb(${colour.r}, ${colour.g}, ${colour.b})`,
      }
    },
    capabilityLabel (capId) {
      const found = this.capabilities.find((c) => c.id === capId)
      return found ? found.label : String(capId)
    },
    capabilityColour (capId) {
      const found = this.capabilities.find((c) => c.id === capId)
      return found ? found.colour : null
    },
    removeCapability (capId) {
      this.selectedCapabilities = (this.selectedCapabilities || []).filter((id) => id !== capId)
    },
    selectedCapabilitiesPayload () {
      return this.capabilities
        .filter((item) => this.isCapabilitySelected(item.id))
        .map((item) => {
          const media = {
            icon: this.getCapabilityIcon(item.id),
            icon_url: this.getCapabilityIconUrl(item.id),
          }
          if (this.selectedProvider === 'infocab') {
            return {
              id: item.id,
              Featureid: item.Featureid != null ? item.Featureid : item.id,
              featureTypeId: item.featureTypeId,
              bitMask: item.bitMask,
              name: item.name,
              ...media,
            }
          }
          return {
            id: item.id,
            name: item.name,
            shortCode: item.shortCode,
            requirement: item.requirement,
            enabled: item.enabled,
            priority: item.priority,
            colour: item.colour,
            excludeFromBroadcast: item.excludeFromBroadcast,
            exclusiveCapability: item.exclusiveCapability,
            operatorOverride: item.operatorOverride,
            rowVersion: item.rowVersion,
            ...media,
          }
        })
    },
    fetchCapabilities () {
      const has = (v) => v !== null && v !== undefined && String(v).trim() !== ''

      if (this.selectedProvider === 'infocab') {
        if (!has(this.userData.infocab_host) || !has(this.userData.infocab_token)) {
          this.snackbar = true
          this.snackbarText = 'Enter Infocab host and token before fetching capabilities'
          return
        }

        this.disableCapabilitiesFetchButton = true
        this.capabilities = []
        this.selectedCapabilities = []
        this.capabilityImages = {}
        this.capabilityIcons = {}

        _getInfoFeatures({
          info_host: this.userData.infocab_host,
          info_token: this.userData.infocab_token,
        })
          .then(
            (response) => {
              this.disableCapabilitiesFetchButton = false
              this.capabilities = this.extractInfoFeatures(response).map(this.mapInfoFeature)
            },
            (error) => {
              this.disableCapabilitiesFetchButton = false
              this.snackbar = true
              this.snackbarText =
                (error.data && error.data.responseMessage) || 'Failed to fetch Infocab features'
            }
          )
          .catch((err) => {
            this.disableCapabilitiesFetchButton = false
            this.snackbar = true
            this.snackbarText = err.message
          })
        return
      }

      if (
        !has(this.userData.api_username) ||
        !has(this.userData.api_password) ||
        !has(this.userData.api_host)
      ) {
        this.snackbar = true
        this.snackbarText = 'Enter Autocab API credentials before fetching capabilities'
        return
      }

      this.disableCapabilitiesFetchButton = true
      this.capabilities = []
      this.selectedCapabilities = []
      this.capabilityImages = {}
      this.capabilityIcons = {}
      const formData = {
        api_username: this.userData.api_username,
        api_password: this.userData.api_password,
        api_host: this.userData.api_host,
      }

      _getCapabilities(formData)
        .then(
          (response) => {
            this.disableCapabilitiesFetchButton = false
            const data = (response.data.obj && response.data.obj.capabilities) || []
            this.capabilities = data.map(this.mapCapability)
          },
          (error) => {
            this.disableCapabilitiesFetchButton = false
            this.snackbar = true
            this.snackbarText =
              (error.data && error.data.responseMessage) || 'Failed to fetch capabilities'
          }
        )
        .catch((err) => {
          this.disableCapabilitiesFetchButton = false
          this.snackbar = true
          this.snackbarText = err.message
        })
    },
    triggerCapabilityImagePick (capId) {
      if (this.capabilityImageUploading[capId]) return
      this.capabilityImageTargetId = capId
      this.$nextTick(() => {
        if (this.$refs.capabilityImageInput) this.$refs.capabilityImageInput.click()
      })
    },
    extractUploadedCapabilityIcon (response) {
      const body = response && response.data ? response.data : {}
      const obj = body.obj || body
      return {
        icon: obj.icon || null,
        icon_url: obj.icon_url || obj.url || obj.image_url || obj.path || null,
      }
    },
    async onCapabilityImageSelected (event) {
      const file = event.target.files && event.target.files[0]
      const capId = this.capabilityImageTargetId
      if (!file || capId == null) return

      try {
        await validateLogoFile(file)

        this.$set(this.capabilityImageUploading, capId, true)
        const formData = new FormData()
        formData.append('icon', file, file.name)
        formData.append('capability_id', capId)

        const response = await _uploadCapabilityImage(formData)
        const uploaded = this.extractUploadedCapabilityIcon(response)
        if (!uploaded.icon_url) throw new Error('Upload succeeded but no icon_url was returned')

        this.$set(this.capabilityImages, capId, uploaded.icon_url)
        if (uploaded.icon) this.$set(this.capabilityIcons, capId, uploaded.icon)
      } catch (err) {
        this.snackbar = true
        this.snackbarText =
          (err && err.data && err.data.responseMessage) ||
          (err && err.message) ||
          'Failed to upload capability image'
      } finally {
        this.$delete(this.capabilityImageUploading, capId)
        this.capabilityImageTargetId = null
        if (this.$refs.capabilityImageInput) this.$refs.capabilityImageInput.value = ''
      }
    },
    clearCapabilityImage (capId) {
      // Keep explicit nulls so update/save payload sends icon_url: null and icon: null
      this.$set(this.capabilityImages, capId, null)
      this.$set(this.capabilityIcons, capId, null)
    },
    hasPaymentAccount (companyName) {
      return !!this.selectedCompanyPaymentAccounts.find((d) => d.company === companyName)
    },
    getPaymentProvider (companyName) {
      const account = this.selectedCompanyPaymentAccounts.find((d) => d.company === companyName)
      return account && account.provider ? account.provider : 'judo'
    },
    getPaymentButtonLabel (companyName) {
      if (!this.hasPaymentAccount(companyName)) return 'Add Payment'
      const provider = this.getPaymentProvider(companyName)
      return provider === 'revolut' ? 'Edit Revolut' : 'Edit Judo'
    },
    isCurrentPaymentAccountValid () {
      const has = (v) => v !== null && v !== undefined && String(v).trim() !== ''
      if ((this.currentPaymentAccount.provider || 'judo') === 'revolut') {
        return has(this.currentPaymentAccount.api_version) && has(this.currentPaymentAccount.base_url) && has(this.currentPaymentAccount.api_secret)
      }
      return has(this.currentPaymentAccount.judo_id) && has(this.currentPaymentAccount.judo_sandbox_url) && has(this.currentPaymentAccount.judo_auth_sandbox) && has(this.currentPaymentAccount.judo_api_version)
    },
    normalizePaymentAccount (company, account = {}) {
      const provider = account.provider === 'revolut' ? 'revolut' : 'judo'
      return {
        company: company || account.company || '',
        provider,
        judo_id: provider === 'judo' ? (account.judo_id || '') : '',
        judo_sandbox_url: provider === 'judo' ? (account.judo_sandbox_url || '') : '',
        judo_api_version: provider === 'judo' ? (account.judo_api_version || '') : '',
        judo_auth_sandbox: provider === 'judo' ? (account.judo_auth_sandbox || '') : '',
        api_version: provider === 'revolut' ? (account.api_version || '') : '',
        base_url: provider === 'revolut' ? (account.base_url || '') : '',
        api_secret: provider === 'revolut' ? (account.api_secret || '') : '',
      }
    },
    toJudoPayload (account) {
      return {
        company: account.company,
        judo_id: account.judo_id,
        judo_sandbox_url: account.judo_sandbox_url,
        judo_api_version: account.judo_api_version,
        judo_auth_sandbox: account.judo_auth_sandbox,
      }
    },
    toRevolutPayload (account) {
      return {
        company: account.company,
        api_version: account.api_version,
        base_url: account.base_url,
        api_secret: account.api_secret,
      }
    },
    fetchICabbiCompanies () {
      this.disableICabbiFetchButton = true
      this.companies = []
      this.selectedCompanies = []
      const formData = {
        icabbi_host: this.userData.icabbi_host,
        icabbi_token: this.userData.icabbi_token,
      }
      _getICabbiCompanies(formData)
        .then(
          (response) => {
            this.disableICabbiFetchButton = false
            this.apiError = ''
            const data = response.data.obj.body.sites
            this.companies = data.map(function (d) {
              return {
                company_id: d.id,
                name: d.title,
                label: d.title,
                value: d.title,
                latitude: 0,
                longitude: 0,
              }
            })
          },
          (error) => {
            this.disableFetchButton = false
            this.disableInfoFetchButton = false
            this.disableICabbiFetchButton = false
            this.snackbar = true
            this.snackbarText = error.data.responseMessage
          }
        )
    },
    fetchInfoCompanies() {
      this.disableInfoFetchButton = true;
      this.companies = []
      this.selectedCompanies = []
      let formData = {
        info_host: this.userData.infocab_host,
        info_token: this.userData.infocab_token,
      }
      _getInfoCompanies(formData)
        .then(
          (response) => {

            this.disableInfoFetchButton = false
            this.apiError = ''
            let data = response.data.obj.divisions
            this.companies = data.map(function (d) {
              return {
                company_id: d.Id,
                name: d.Name,
                label: d.Name,
                value: d.Name,
                latitude: (d.latitude !== undefined && d.latitude !== null) ? d.latitude : 0,
                longitude: (d.longitude !== undefined && d.longitude !== null) ? d.longitude : 0,
                info_parent_id : d.CompanyId

              }
            })



          },
          (error) => {
            this.disableFetchButton = false
            this.disableInfoFetchButton = false
            this.snackbar = true
            this.snackbarText = error.data.responseMessage
          });
    },
    fetchCompanies() {
      this.disableFetchButton = true
      this.companies = []
      this.selectedCompanies = []
      let formData = {
        api_username: this.userData.api_username,
        api_password: this.userData.api_password,
        api_host: this.userData.api_host,
      }

      _getCompanies(formData)
        .then(
          (response) => {
            this.disableFetchButton = false
            this.apiError = ''
            let data = response.data.obj.companies
            this.companies = data.map(function (d) {
              return {
                company_id: d.id,
                name: d.name,
                label: d.name,
                value: d.name,
                latitude: d.location.latitude,
                longitude: d.location.longitude,
              }
            })

            console.log(this.companies)
          },
          (error) => {
            this.disableFetchButton = false
            this.snackbar = true
            this.snackbarText = error.data.responseMessage
          }
        )
        .catch((err) => {
          this.disableFetchButton = false
          this.snackbar = true
          this.snackbarText = err.message
        })
    },
    save() {
      var self = this
      let companiesObjArray = this.companies
      let sel = this.selectedCompanies
      var filteredCompaniesObj = companiesObjArray.filter((item) => sel.includes(item.name))

      if (filteredCompaniesObj.length) {
        this.userData.companies = filteredCompaniesObj.map((v) => ({
          ...v,
          id: v.company_id,
          judo_account: (() => {
            const paymentAccount = self.selectedCompanyPaymentAccounts.find((item) => item.company === v.name)
            return paymentAccount && paymentAccount.provider === 'judo' ? self.toJudoPayload(paymentAccount) : null
          })(),
          revolut_account: (() => {
            const paymentAccount = self.selectedCompanyPaymentAccounts.find((item) => item.company === v.name)
            return paymentAccount && paymentAccount.provider === 'revolut' ? self.toRevolutPayload(paymentAccount) : null
          })(),
        }))
        this.userData.services = this.selectedServices
        this.userData.features = this.selectedFeatures
        this.userData.capabilities = this.supportsCapabilities
          ? this.selectedCapabilitiesPayload()
          : []
        this.userData.allowManual =
          this.userData.allowManual == 'true' || this.userData.allowManual == 1 ? 'true' : 'false'

          this.userData.provider = this.selectedProvider;
          if (this.selectedProvider === 'infocab') {
            this.userData.api_username = null;
            this.userData.api_host = null;
            this.userData.api_password = null;
            this.userData.icabbi_host = null
            this.userData.icabbi_token = null
          }
          else if (this.selectedProvider === 'icabbi') {
            this.userData.api_username = null;
            this.userData.api_host = null;
            this.userData.api_password = null;
            this.userData.infocab_host = null;
            this.userData.infocab_token = null;
          }
          else {
            this.userData.infocab_host = null;
            this.userData.infocab_token = null;
            this.userData.icabbi_host = null
            this.userData.icabbi_token = null
          }
        _saveAdmin(this.userData)
          .then(
            () => {
              this.$router.push('/users')
            },
            (err) => {
              if (err.data && err.data.status) {
                switch (err.data.status) {
                  case 422:
                    this.inputErrors = err.data.errors // assign field errors
                    this.formErrors = err.data.message
                    return
                  // ... add lots more cases of bad things that can happen here
                }
              }
            }
          )
          .catch((err) => {
            console.log(err.data)
          })
      } else {
        alert('please fetch companies if not and assign')
      }
    },
    getServices() {
      _getServices().then((res) => {
        let objArray1 = res.data.obj
        this.services = objArray1.map((v) => ({
          ...v,
          value: v.id,
          label: v.name,
        }))
      })
    },

    openPaymentAccountDialog(company) {
      this.dialog = true

      var value = this.selectedCompanyPaymentAccounts.find((data) => data.company === company)
      if (value) {
        this.currentPaymentAccount = this.normalizePaymentAccount(company, value)
        this.editingPaymentAccount = true
      } else {
        this.editingPaymentAccount = false
        this.currentPaymentAccount = this.normalizePaymentAccount(company, { provider: 'judo' })
      }
    },

    savePaymentCredentials() {
      const normalizedAccount = this.normalizePaymentAccount(this.currentPaymentAccount.company, this.currentPaymentAccount)
      const accountIndex = this.selectedCompanyPaymentAccounts.findIndex((data) => data.company === normalizedAccount.company)
      if (accountIndex + 1) {
        this.$set(this.selectedCompanyPaymentAccounts, accountIndex, normalizedAccount)
      } else {
        this.selectedCompanyPaymentAccounts.push(normalizedAccount)
      }
      this.dialog = false
    },

    removePaymentCredentials(company) {
      this.selectedCompanyPaymentAccounts = this.selectedCompanyPaymentAccounts.filter(
        (scj) => scj.company != company
      )

      this.dialog = false
    },
  },
}
</script>

<style scoped>
.provider-form {
  margin-bottom: 0 !important;
}
.add-user-container {
  padding: 16px;
}

.main-card-container {
  overflow: hidden !important;
}

.main-card-container >>> .v-card__text {
  overflow: visible;
}

.form-row {
  margin: 0;
  align-items: stretch;
  padding-bottom: 35px;
}

.form-section-card {
  height: 100%;
  border-radius: 8px;
  transition: all 0.3s ease;
  margin-bottom: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.form-section-card >>> .v-card__text {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.form-section-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.v-row {
  margin: 0;
}

.v-col {
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
}

.section-title {
  background: linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%);
  border-bottom: 2px solid #e0e0e0;
  padding: 16px 20px !important;
  font-weight: 600;
  font-size: 16px;
  color: #424242;
}

.section-title .v-icon {
  margin-right: 8px;
}

.save-button-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 0;
  border-top: 2px solid #e0e0e0;
  margin-top: 16px;
}

.user-form-card {
  position: relative;
}

.top-right-save {
  position: absolute;
  top: 18px;
  right: 28px;
  /* z-index: 5; */
}

.top-right-save .save-user-btn {
  padding: 10px 18px !important;
  min-width: auto;
}

@media (max-width: 600px) {
  .top-right-save {
    position: static;
    margin: 10px 0 6px;
    display: flex;
    justify-content: flex-end;
  }
}

.save-user-btn {
  border-radius: 8px;
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.5px;
  padding: 16px 48px !important;
  min-width: 200px;
  box-shadow: 0 4px 12px rgba(63, 81, 181, 0.25);
  transition: all 0.3s ease;
  background-color: #3f51b5 !important;
  color: #ffffff !important;
}



.save-user-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #e0e0e0 !important;
  color: #9e9e9e !important;
}

.save-hint {
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ff9800;
}

.fetch-btn {
  border-radius: 6px;
  text-transform: none;
  font-weight: 500;
  margin-top: 8px;
}

.capabilities-card >>> .capabilities-card-body {
  justify-content: flex-start !important;
}

.capabilities-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}

.capabilities-header__left {
  display: flex;
  align-items: center;
  min-width: 0;
}

.cap-count-chip {
  font-weight: 600;
}

.cap-refresh-btn {
  text-transform: none;
  font-weight: 500;
}

.cap-picker {
  max-width: 560px;
}

.cap-empty {
  text-align: center;
  padding: 32px 20px;
  border: 1px dashed #d5d8e8;
  border-radius: 10px;
  background: #fafbff;
  max-width: 560px;
  margin: 4px auto 8px;
}

.cap-empty__icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 10px;
  border-radius: 50%;
  background: #e8eaf6;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cap-empty__title {
  font-size: 15px;
  font-weight: 600;
  color: #424242;
}

.cap-empty__subtitle {
  font-size: 13px;
  color: #757575;
  margin-top: 4px;
  max-width: 380px;
  margin-left: auto;
  margin-right: auto;
}

.cap-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
}

.cap-tile {
  position: relative;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  background: #ffffff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.cap-tile:hover {
  border-color: #c5cae9;
  box-shadow: 0 3px 10px rgba(63, 81, 181, 0.12);
}

.cap-tile--filled {
  border-color: #c8e6c9;
}

.cap-tile__deselect-wrap {
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 3;
}

.cap-tile__deselect {
  background: rgba(255, 255, 255, 0.95) !important;
}

.cap-tile__logo {
  position: relative;
  height: 96px;
  background: #fafafa;
  border-bottom: 1px dashed #e0e0e0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  outline: none;
}

.cap-tile__logo:focus {
  box-shadow: inset 0 0 0 2px #9fa8da;
}

.cap-tile__logo--uploading {
  cursor: progress;
  pointer-events: none;
}

.cap-tile__logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 8px;
  background: #ffffff;
}

.cap-tile__hint {
  font-size: 12px;
  color: #9e9e9e;
}

.cap-tile__overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(33, 33, 33, 0.55);
  color: #ffffff;
  font-size: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  opacity: 0;
  transition: opacity 0.18s ease;
}

.cap-tile__logo:hover .cap-tile__overlay,
.cap-tile__logo:focus .cap-tile__overlay {
  opacity: 1;
}

.cap-tile__body {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
}

.cap-tile__swatch {
  width: 10px;
  height: 10px;
}

.cap-tile__name {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 500;
  color: #424242;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cap-tile__clear {
  flex-shrink: 0;
}

/* Companies + Judo compact UX */
.companies-picker {
  margin-top: 6px;
}

.companies-field-label {
  margin-bottom: 8px;
}

.capability-option {
  display: flex;
  align-items: center;
  gap: 8px;
}

.capability-swatch {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  flex-shrink: 0;
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.companies-picker >>> .v-chip {
  max-width: 100%;
}

.judo-compact {
  border-top: 1px solid #eeeeee;
  padding-top: 12px;
}

.judo-list {
  max-height: 220px;
  overflow-y: auto;
  padding-right: 6px;
}

.judo-list::-webkit-scrollbar {
  width: 6px;
}

.judo-list::-webkit-scrollbar-thumb {
  background: #d0d0d0;
  border-radius: 6px;
}

.judo-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px dashed #eeeeee;
}

.judo-row:last-child {
  border-bottom: none;
}

.judo-company-name {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 500;
  color: #424242;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.judo-action-btn {
  flex-shrink: 0;
  text-transform: none;
}

.sms-gateway-element-class {
  display: flex;
  justify-content: space-around;
  align-items: center;
}




.judo-dialog {
  border-radius: 12px;
  overflow: hidden;
}

.judo-dialog >>> .formulate-input {
  margin-bottom: 12px;
}

.payment-dialog {
  border: 1px solid #e8eaf6;
}

.payment-dialog__header {
  position: relative;
  display: flex;
  align-items: center;
  padding: 16px 52px 16px 20px;
  background: #ffffff;
  border-bottom: 1px solid #eeeeee;
}

.payment-dialog__heading-wrap {
  min-width: 0;
}

.payment-dialog__title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.3;
}

.payment-dialog__subtitle {
  margin-top: 2px;
  font-size: 13px;
  color: #757575;
  line-height: 1.4;
  word-break: break-word;
}

.payment-dialog__content {
  padding: 20px !important;
}

.payment-section {
  border: 1px solid #eceff5;
  border-radius: 10px;
  padding: 14px;
  background: #fafbff;
}

.payment-section--credentials {
  margin-top: 14px;
}

.payment-section__title {
  font-size: 14px;
  font-weight: 600;
  color: #253238;
}

.payment-section__subtitle {
  margin-top: 2px;
  margin-bottom: 8px;
  font-size: 12px;
  color: #6b7280;
}

.payment-provider-radio {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.payment-dialog__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.payment-action-btn {
  text-transform: none;
  min-width: 122px;
}

.payment-remove-btn {
  border-color: #ef5350 !important;
}

.payment-dialog >>> .payment-full-width-input,
.payment-dialog >>> .payment-full-width-input .formulate-input-wrapper,
.payment-dialog >>> .payment-full-width-input .formulate-input-element,
.payment-dialog >>> .payment-full-width-input .formulate-input-element input {
  width: 100%;
  max-width: 100%;
}

.judo-close-btn {
  position: absolute;
  top: 10px;
  right: 12px;
}

@media (max-width: 960px) {
  .add-user-container {
    padding: 8px;
  }

  .save-btn-header {
    margin-top: 8px;
    width: 100%;
  }

  .payment-dialog__content {
    padding: 16px !important;
  }

  .payment-section {
    padding: 12px;
  }

  .payment-dialog__actions {
    justify-content: stretch;
  }

  .payment-action-btn {
    flex: 1 1 100%;
    min-width: 100%;
  }
}
</style>

<style>
/* Form field styling */
.formulate-input {
  margin-bottom: 20px;
}

.formulate-input-label {
  font-weight: 500;
  color: #424242;
  margin-bottom: 8px;
  font-size: 14px;
}

.formulate-input-element {
  border-radius: 6px;
}

.formulate-input-wrapper {
  margin-bottom: 8px;
}

/* Button styling */
.formulate-input[data-type="submit"],
.formulate-input[data-type="button"] {
  margin-top: 8px;
}

.formulate-input[data-type="submit"] button,
.formulate-input[data-type="button"] button {
  border-radius: 6px;
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.custom-submit-btn button {
  background-color: #3f51b5 !important;
  color: white !important;
  padding: 12px 32px !important;
  font-size: 16px !important;
  min-width: 150px !important;
}

.custom-submit-btn button:hover {
  background-color: #303f9f !important;
}

.custom-submit-btn button:disabled {
  background-color: #e0e0e0 !important;
  color: #9e9e9e !important;
}

/* Radio and checkbox styling */
.formulate-input[data-type="radio"] .formulate-input-element,
.formulate-input[data-type="checkbox"] .formulate-input-element {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

/* Group styling */
.formulate-input[data-type="group"] {
  background-color: transparent;
  padding: 0;
  border-radius: 0;
  border: none;
}

/* Vue Formulate "group" sometimes renders its slot content in a horizontal flex row,
   which makes our alert "shrink-to-fit". Force group content to stack vertically. */
.formulate-input[data-type="group"] .formulate-input-element {
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

/* Card text styling */
.form-section-card >>> .v-card__text {
  padding: 20px !important;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.form-section-card >>> .v-card__text > *:last-child {
  margin-bottom: 0;
}

/* Credentials Alert styling */
.credentials-alert {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(135deg, rgba(63, 81, 181, 0.08) 0%, rgba(63, 81, 181, 0.04) 100%);
  border-left: 4px solid #3f51b5;
  border-radius: 6px;
  margin-bottom: 16px;
}

.credentials-alert-text {
  font-size: 13px;
  font-weight: 500;
  color: #424242;
  line-height: 1.4;
}

.credentials-alert .v-icon {
  flex-shrink: 0;
}

.credentials-alert-green {
  background: linear-gradient(135deg, rgba(63, 81, 181, 0.08) 0%, rgba(63, 81, 181, 0.04) 100%) !important;
  border-left-color: #3f51b5 !important;
}

.user-info-fields .formulate-input-label,
.user-info-fields .checkbox-field .formulate-input-label {
  font-size: 14px;
  font-weight: 500;
  color: #424242;
  margin-bottom: 8px;
  line-height: 1.4;
}

.user-info-fields .formulate-input input,
.user-info-fields .formulate-input [type="email"],
.user-info-fields .formulate-input [type="password"],
.user-info-fields .formulate-input [type="text"] {
  font-size: 14px;
  line-height: 1.4;
}

.user-info-fields .formulate-input[data-type="radio"] label,
.user-info-fields .formulate-input[data-type="radio"] .formulate-input-element label,
.user-info-fields .custom-checkbox__label {
  font-size: 14px;
  font-weight: 400;
  color: #424242;
  line-height: 1.4;
}

.checkbox-field {
  margin-top: 0;
  margin-bottom: 20px;
}

.checkbox-field .formulate-input-label {
  margin-bottom: 8px;
}

.allow-manual-field {
  margin-bottom: 0 !important;
}

.feature-access-field {
  margin-top: 20px;
  margin-bottom: 20px !important;
}

.custom-checkbox-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.custom-checkbox {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
  width: fit-content;
}

.custom-checkbox__input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}

.custom-checkbox__box {
  width: 18px;
  height: 18px;
  border: 2px solid #bdbdbd;
  border-radius: 4px;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
}

.custom-checkbox__check {
  color: #ffffff !important;
  opacity: 0;
  transform: scale(0.6);
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.custom-checkbox__label {
  font-size: 14px;
  font-weight: 400;
  color: #424242;
  line-height: 1.4;
}

.custom-checkbox:hover .custom-checkbox__box {
  border-color: #4caf50;
}

.custom-checkbox__input:focus-visible + .custom-checkbox__box {
  box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.25);
}

.custom-checkbox__input:checked + .custom-checkbox__box {
  border-color: #4caf50;
  background: #4caf50;
}

.custom-checkbox__input:checked + .custom-checkbox__box .custom-checkbox__check {
  opacity: 1;
  transform: scale(1);
}

/* Alert styling */
.v-alert {
  border-radius: 6px;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .formulate-input[data-type="radio"] .formulate-input-element,
  .formulate-input[data-type="checkbox"] .formulate-input-element {
    flex-direction: column;
    gap: 8px;
  }

  .form-section-card {
    margin-bottom: 16px;
  }
}
</style>
